import { useMemo } from 'react';
import { useProjectPreviewRouteData } from './use-project-preview-route-data';
import {
    extractProjectImages,
    filterImagesByModuleType,
    sortImagesByOrder,
} from '../util/extract-project-images';
import { FlexibleModuleEnum } from 'generated/graphql';

/**
 * Hook to extract and manage images from project page modules
 *
 * @returns Object containing various image arrays and utility functions
 */
// eslint-disable-next-line import/no-unused-modules
export const useProjectImages = () => {
    const project = useProjectPreviewRouteData();

    const allImages = useMemo(() => {
        if (!project?.Modules) {
            return [];
        }
        return extractProjectImages(project.Modules);
    }, [project?.Modules]);

    const sortedImages = useMemo(() => {
        return sortImagesByOrder(allImages);
    }, [allImages]);

    const imageModuleImages = useMemo(() => {
        return filterImagesByModuleType(sortedImages, [
            FlexibleModuleEnum.SingleImageModule,
            FlexibleModuleEnum.DuoImageModule,
            FlexibleModuleEnum.QuadImageModule,
            FlexibleModuleEnum.OctaImageModule,
        ]);
    }, [sortedImages]);

    const imageParagraphImages = useMemo(() => {
        return filterImagesByModuleType(sortedImages, [
            FlexibleModuleEnum.ImageParagraphModule,
        ]);
    }, [sortedImages]);

    const videoThumbnails = useMemo(() => {
        return filterImagesByModuleType(sortedImages, [
            FlexibleModuleEnum.SingleVideoModule,
            FlexibleModuleEnum.VideoParagraphModule,
        ]);
    }, [sortedImages]);

    // Get all videos (including those from product images)
    const allVideos = useMemo(() => {
        return sortedImages.filter((image) => image.videoUrl);
    }, [sortedImages]);

    return {
        /** All images and videos from the project (excluding product modules) - unsorted */
        allImages,
        /** All images and videos from the project (excluding product modules) - sorted */
        sortedImages,
        /** Images from dedicated image modules (SingleImage, DuoImage, QuadImage, OctaImage) */
        imageModuleImages,
        /** Images from image-paragraph modules */
        imageParagraphImages,
        /** Video thumbnails from video modules */
        videoThumbnails,
        /** All videos */
        allVideos,
        /** Total count of all images and videos */
        totalCount: allImages.length,
        /** Count of all videos */
        videoCount: allVideos.length,
        /** Utility function to filter by custom module types */
        filterByModuleType: (moduleTypes: string[]) =>
            filterImagesByModuleType(sortedImages, moduleTypes),
    };
};
